import { SimpleTreeView } from "@mui/x-tree-view-pro";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { createPortal } from "react-dom";
import hasChildren from "./helperfunctions";
import CustomTreeItem from "./CustomTreeItem";
import { Box, Divider, Typography } from "@mui/material";
import { CancelOutlined, Search } from "@mui/icons-material";
import GenericActionMenu from "../menu/GenericActionMenu";
import { FolderIcon } from '@heroicons/react/24/outline';
import { breakpoints } from "../../theme/theme";
import styles from "../../styles/folderDrawerView/index.module.scss";
import { InputBox } from "../FormComponent";
import ModuleEmptyScreen from "../TestCase/ModuleEmptyScreen";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { useModuleReorder } from "./reorder/useModuleReorder";
import { preserveLayoutStrategy } from "./reorder/preserveLayoutStrategy";
import { snapTopLeftToCursor } from "./reorder/snapTopLeftToCursor";
import { ModuleTreeNode, SortableModuleTreeNode, ModuleName } from "./reorder/ModuleTreeNode";

const DEFAULT_MODULE_TITLE = "All Test Cases";
const DEFAULT_TITLE = "Project Name";

const FolderDrawerTreeView = forwardRef(({
  data,
  moduleTitle = DEFAULT_MODULE_TITLE,
  title = DEFAULT_TITLE,
  onLeafNodeClick,
  rowActionButtons = (() => { return [] }),
  headerActionButtons = [],
  disableHeaderActions = true,
  disableActions = true,
  selected = ['default'],
  titleLabel = 'Project Name',
  setSelected = () => { },
  hideHeader = false,
  showSearch = false,
  smallView = false,
  isDataLoading = false,
  updateIsExpanded = () => { },
  highlightedNodeIds = null,
  moduleEmptyPrompt = null,
  reorderable = false,
  onReorder = () => { },
}, ref) => {

  const [expanded, setExpanded] = useState([]);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  const searchActive = searchTerm.trim().length > 0;
  const dndActive = reorderable && !searchActive;

  const {
    sensors,
    collisionDetection,
    measuring,
    activeId,
    activeLabel,
    activeParentId,
    dropFlashId,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useModuleReorder({
    enabled: dndActive,
    onReorder,
    treeData: filteredData,
    expanded,
    setExpanded,
  });

  useEffect(() => {
    setFilteredData([...data]);
  }, [data])

  useImperativeHandle(ref, () => ({
    triggerExpandAll: expandAll,
    triggerCollapseAll: collapseAll,
    triggerExpandOneNode: expandOneNode,
  }), []);

  const expandOneNode = (nodeId) => {
    if (!expanded.includes(String(nodeId))) {
      setExpanded([...expanded, String(nodeId)]);
    }
  }

  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const getAllIds = (nodes) => {
    let ids = [];
    nodes.forEach(node => {
      ids.push(String(node.id));
      if (node.children?.length) {
        ids = [...ids, ...getAllIds(node.children)];
      }
    });
    return ids;
  };

  const expandAll = (treeData) => {
    const allIds = getAllIds(treeData);
    setExpanded(allIds);
    updateIsExpanded(true);
  };

  const collapseAll = () => {
    setExpanded([]);
    updateIsExpanded(false);
  };

  const filterTreeData = (nodes, query) => {
    if (!query.trim()) return nodes;

    return nodes
      .map((node) => {
        if (node.name.toLowerCase().includes(query.toLowerCase())) {
          return node;
        }
        if (node.children?.length) {
          const filteredChildren = filterTreeData(node.children, query);
          if (filteredChildren.length > 0) {
            return { ...node, children: filteredChildren };
          }
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleSearchTerm = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = filterTreeData(data, value);
      setFilteredData(filtered);

      if (filtered.length) {
        const allNodeIds = getAllIds(filtered);
        setExpanded([...allNodeIds]);
      }
    }
  };

  const sharedNodeProps = {
    expanded,
    setExpanded,
    hoveredNodeId,
    setHoveredNodeId,
    onLeafNodeClick,
    disableActions,
    rowActionButtons,
    activeId,
    activeParentId,
    dropFlashId,
  };

  const renderTree = (nodes, level = 1, parentId = null) => {
    const siblingIds = nodes.filter((n) => n?.id).map((n) => String(n.id));

    const items = nodes.map((node) => {
      if (!node?.id) {
        console.error("Node is missing an id:", node);
        return null;
      }

      const childTree = hasChildren(node)
        ? renderTree(node.children, level + 1, node.id)
        : null;

      const showDragHandle = dndActive && hoveredNodeId === node.id;

      if (dndActive) {
        return (
          <SortableModuleTreeNode
            key={node.id}
            node={node}
            level={level}
            parentId={parentId}
            siblingIds={siblingIds}
            showDragHandle={showDragHandle}
            {...sharedNodeProps}
          >
            {childTree}
          </SortableModuleTreeNode>
        );
      }

      return (
        <ModuleTreeNode
          key={node.id}
          node={node}
          level={level}
          {...sharedNodeProps}
        >
          {childTree}
        </ModuleTreeNode>
      );
    });

    if (!dndActive) {
      return items;
    }

    return (
      <SortableContext
        items={siblingIds}
        strategy={preserveLayoutStrategy}
      >
        {items}
      </SortableContext>
    );
  };

  function handleClearSearchTerm() {
    setSearchTerm('');
    setFilteredData(data)
  }

  const showModuleEmpty = Boolean(moduleEmptyPrompt) && !isDataLoading && data.length === 0;

  const treeView = (
    <SimpleTreeView
      id="module-explorer"
      aria-label="modules-tree"
      sx={{
        overflowX: "hidden",
        overflowY: "auto",
        px: "4px",
        py: "0px",
        flex: 1,

        "& .MuiTreeItem-iconContainer": {
          display: 'none !important',
        },

        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.05)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.3)',
          },
        },

      }}
      expandedItems={expanded}
      onExpandedItemsChange={handleNodeToggle}
      selectedItems={selected}
      onSelectedItemsChange={(event, newSelected) => { setSelected(newSelected) }}
    >
      {!hideHeader && (
        <>
          <CustomTreeItem
            itemId="default"
            level={1}
            label={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  height: 'max-content',
                  minHeight: '25px',
                  gap: '6px',
                  [`@media (min-width:${breakpoints.xl}px)`]: {
                    py: "0px",
                    minHeight: '35px',
                  },
                }}
                onClick={(e) => {
                  if (e.target.closest(".module-action-menu") === null) {
                    onLeafNodeClick("default", moduleTitle);
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    overflow: "hidden",
                    flex: 1,
                  }}
                >
                  <span className="icon-small" />
                  <Box
                    className={'icon-cover-small bg-blue100'}
                  >
                    <FolderIcon
                      className="icon-small blue600"
                    />
                  </Box>
                  <ModuleName
                    name={moduleTitle}
                    color={'#1e293b'}
                    fontWeight={500}
                  />
                </Box>
              </Box>
            }
          />
        </>
      )}
      {renderTree(filteredData)}
    </SimpleTreeView>
  );

  return (
    <Box className={`${styles.container} bg-white`}>
      {!hideHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1.25 }}>
            <Box sx={{ minWidth: 6, width: 6, height: 6, backgroundColor: '#3b82f6', borderRadius: '50%' }} />
            <Typography
              variant="componentTitle"
              className={`ellipsis-container-line-2 ${styles.titleContainer}`}
              sx={{
                color: '#0f172a',
              }}
              title={title}
            >
              {title}
            </Typography>
          </Box>
          {!disableHeaderActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '10px' }}>
              <GenericActionMenu menuItems={headerActionButtons} />
            </Box>
          )}
        </Box>
      )}
      <Divider className={`${styles.divider}`} />
      <Box className={`${styles.innerContainer}`}>
        {showSearch && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px',
          }}>
            <InputBox
              label="Modules"
              value={searchTerm}
              onChange={handleSearchTerm}
              variant="outlined"
              placeholder="Search Modules"
              startIcon={<Search className="icon-small" />}
              endIcon={searchTerm && <CancelOutlined className="icon-small" />}
              onEndIconClick={handleClearSearchTerm}
            />
          </div>
        )}

        {isDataLoading ? null : showModuleEmpty ? (
          <Box className={styles.moduleEmptyWrap}>
            <ModuleEmptyScreen onAddModule={moduleEmptyPrompt.onAddModule} />
          </Box>
        ) : dndActive ? (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            measuring={measuring}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            {treeView}
            {typeof document !== 'undefined' && createPortal(
              <DragOverlay
                dropAnimation={null}
                modifiers={[snapTopLeftToCursor]}
                style={{ cursor: 'grabbing' }}
              >
                {activeId ? (
                  <Box
                    data-testid="module-drag-overlay"
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      backgroundColor: 'rgba(255, 255, 255, 0.82)',
                      border: '1px solid rgba(226, 232, 240, 0.95)',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#1e293b',
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    {activeLabel || 'Module'}
                  </Box>
                ) : null}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        ) : (
          treeView
        )}
      </Box>
    </Box>
  );
});

export default FolderDrawerTreeView;
